import React, { useCallback, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  FormGroup,
  FormControlLabel,
  useTheme,
} from "@mui/material";

import {
  ButtonType,
  CustomLink,
  CustomSelect,
  CustomTextField,
  getDateOnly,
} from "@BehpardazBarin/frontend_components";
import {
  customInputProps,
  customLinkProps,
  customSelectProps,
} from "../constants/Style";
import { useMutation, useQuery } from "@tanstack/react-query";
import { GetAllReligions, register } from "../services/Person";
import { CacheNames, CityAutocompleteSearchType } from "../constants/Constants";
import { CityAutoComplete, GetAllCountries } from "../services/City";
import { DatePicker } from "@mui/x-date-pickers";
import CustomAutoComplete from "../components/Autocomplete/CustomAutoComplete";
import { snackActions } from "../utils/SnackbarUtils";
import { useNavigate } from "react-router";
import { PrimaryCustomButton } from "../components/CustomButtons/PrimaryCustomButton";
const Register: React.FC = () => {
  const theme = useTheme();
  const [religionId, setReligionId] = useState<string | number>("");
  const [name, setName] = useState<string | number>("");
  const [family, setFamily] = useState<string | number>("");
  const [fatherName, setFatherName] = useState<string | number>("");
  const [nationalCode, setNationalCode] = useState<string | number>("");
  const [mobile, setMobile] = useState<string | number>("");
  const [gender, setGender] = useState<string | number>("");
  const [nationalityId, setNationalityId] = useState<string | number>("");
  const [citizenshipId, setCitizenshipId] = useState<string | number>("");
  const [birthDate, setBirthDate] = useState<Date | null | undefined>(null);
  const [selectedBirthPlace, setSelectedBirthPlace] = React.useState(null);

  const registerMutation = useMutation({
    mutationFn: register,
  });

  const navigate = useNavigate();
  const handleSaveRegister = () => {
    if (!name) return snackActions.error("نام را وارد کنید");
    if (!family) return snackActions.error("نام خانوادگی را وارد کنید");
    if (!fatherName) return snackActions.error("نام پدر را وارد کنید");
    if (!nationalCode && nationalityId === 1)
      return snackActions.error("کد ملی را وارد کنید");
    if (!gender) return snackActions.error("جنسیت را انتخاب کنید");
    if (!mobile) return snackActions.error("شماره موبایل را وارد کنید");
    if (!birthDate) return snackActions.error("تاریخ تولد را وارد کنید");
    if (!religionId) return snackActions.error("مذهب را انتخاب کنید");
    if (!nationalityId) return snackActions.error("تابعیت را انتخاب کنید");
    if (!citizenshipId) return snackActions.error("ملیت را انتخاب کنید");
    if (!selectedBirthPlace)
      return snackActions.error("محل تولد را انتخاب کنید");
    const jalaliBirthDate = getDateOnly(birthDate?.toISOString());
    const params = {
      name: name,
      family: family,
      fatherName: fatherName,
      nationalCode: nationalCode,
      gender: gender,
      mobile: mobile,
      birthDate: jalaliBirthDate,
      religionId: religionId,
      nationalityId: nationalityId,
      citizenshipId: citizenshipId,
      birthPlaceId: selectedBirthPlace ? (selectedBirthPlace as any).id : 0,
    };

    //console.log("params", params);

    registerMutation.mutate(params, {
      onSuccess: (response) => {
        navigate("/login");
      },
    });
  };
  const genderOptions = [
    { title: "مرد", value: 1 },
    { title: "زن", value: 2 },
  ];

  const getAllReligionsQuery = useQuery({
    queryKey: [CacheNames.GetAllReligions],
    queryFn: GetAllReligions,
  });
  const getAllCountriesQuery = useQuery({
    queryKey: [CacheNames.GetAllCountries],
    queryFn: GetAllCountries,
  });

  interface selectOptionType {
    title: string;
    value: number | string;
  }
  interface Country {
    title: string;
    id: number;
  }
  let countries: selectOptionType[] = [];

  interface Religion {
    title: string;
    id: number;
  }
  let religions: selectOptionType[] = [];

  if (
    getAllCountriesQuery &&
    !getAllCountriesQuery.isLoading &&
    getAllCountriesQuery.data
  ) {
    const countriesData = getAllCountriesQuery.data as unknown as Country[];
    countriesData?.forEach((item: Country, index: number) => {
      countries[index] = { title: item.title, value: item.id };
    });
  }
  if (
    getAllReligionsQuery &&
    !getAllReligionsQuery.isLoading &&
    getAllReligionsQuery.data
  ) {
    const religionsData = getAllReligionsQuery.data as unknown as Religion[];
    religionsData?.forEach((item: Religion, index: number) => {
      religions[index] = { title: item.title, value: item.id };
    });
  }

  //search birthplace autocomplete callbacks
  const handleSearchBirthPlace = useCallback((value: any) => {
    setSelectedBirthPlace(value);
  }, []);
  const searchBirthPlaceQuery = useCallback((debounceValue: string) => {
    return CityAutoComplete({
      name: debounceValue,
      searchType: CityAutocompleteSearchType.Fuzzy,
    });
  }, []);
  const getBirthPlaceOptionLabel = useCallback((option: any) => {
    return `${option?.title ?? ""}`;
  }, []);
  const ReadOnlyServiceComponent = () => (
    <Typography>{getBirthPlaceOptionLabel(selectedBirthPlace)}</Typography>
  );

  return (
    <>
      <Box mb={4}>
        <Typography
          variant="h6"
          gutterBottom
          textAlign="center"
          sx={{ fontFamily: "IRANSansXBold" }}
        >
          ساخت حساب کاربری
        </Typography>
        <Typography
          variant="body2"
          textAlign="center"
          sx={{ color: theme.palette.text.secondary, fontFamily: "IRANSansX" }}
        >
          ‌برای ثبت‌نام، اطلاعات زیر را وارد کنید.
        </Typography>
      </Box>
      <Grid container rowSpacing={1} columnSpacing={{ xs: 0.5, sm: 0, md: 0 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <CustomTextField
            label="نام "
            value={name}
            placeholder={"نام خود را وارد کنید..."}
            onChange={setName}
            {...customInputProps(theme)}
            autoFocus={true}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <CustomTextField
            label={"نام خانوادگی"}
            placeholder={"نام خانوادگی خود را وارد کنید..."}
            onChange={setFamily}
            value={family}
            {...customInputProps(theme)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <CustomTextField
            label={"نام پدر"}
            placeholder={"نام پدر را وارد کنید..."}
            onChange={setFatherName}
            value={fatherName}
            {...customInputProps(theme)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <CustomTextField
            label={"کد ملی"}
            placeholder={"کد ملی خود را وارد کنید..."}
            onChange={setNationalCode}
            value={nationalCode}
            {...customInputProps(theme)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <CustomSelect
            onChange={setGender}
            label="جنسیت"
            options={genderOptions}
            value={gender}
            {...customSelectProps(theme)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <CustomTextField
            label={"موبایل"}
            placeholder={"موبایل خود را وارد کنید..."}
            onChange={setMobile}
            value={mobile}
            {...customInputProps(theme)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormGroup style={{ marginBottom: "16px" }}>
            <FormControlLabel
              control={<Typography variant="h6" />}
              label={
                <Typography
                  style={{
                    fontFamily: "IRANSansX",
                    fontSize: "16px",
                    marginBottom: 4,
                  }}
                >
                  تاریخ تولد
                </Typography>
              }
              style={{ margin: 0, alignItems: "start" }}
            />

            <DatePicker
              value={birthDate}
              onChange={setBirthDate}
              slotProps={{
                textField: {
                  placeholder: "روز / ماه / سال",
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      fontFamily: "IRANSansX",
                      backgroundColor:
                        theme.customColors.neutral.surfaceContainerLowest,
                      height: "48px",
                      mr: "12px",
                    },
                  },
                },
              }}
            />
          </FormGroup>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <CustomSelect
            onChange={setReligionId}
            label="مذهب"
            options={religions}
            value={religionId}
            {...customSelectProps(theme)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <CustomSelect
            onChange={setNationalityId}
            label="تابعیت"
            options={countries}
            value={nationalityId}
            {...customSelectProps(theme)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <CustomSelect
            onChange={setCitizenshipId}
            label="ملیت"
            options={countries}
            value={citizenshipId}
            {...customSelectProps(theme)}
          />
        </Grid>
        <Grid size={12} sx={{ pr: "10px" }}>
          <Typography
            sx={{
              fontSize: "16px",
              marginBottom: "8px",
              marginLeft: "8px",
              fontFamily: "IRANSansX",
            }}
          >
            محل تولد
          </Typography>
          <CustomAutoComplete
            queryFn={searchBirthPlaceQuery}
            onHandleSearch={handleSearchBirthPlace}
            label="محل تولد را وارد کنید ..."
            getOptionLabel={getBirthPlaceOptionLabel}
            selectedOptionValue={selectedBirthPlace ? selectedBirthPlace : null}
            readOnly={false}
            ReadOnlyComponent={ReadOnlyServiceComponent}
          />

          {selectedBirthPlace && (
            <Typography
              sx={{
                borderRadius: "16px",
                mt: "12px",
                padding: "15px",
                height: "18px",
                marginBottom: "16px",
                fontFamily: "IRANSansX",
                color: theme.customColors.neutral[50],
                fontSize: "12px",
                background: theme.customColors.neutral[98],
              }}
            >
              {(selectedBirthPlace as any)?.fullName ?? ""}
            </Typography>
          )}
        </Grid>
        <Grid size={12}>
          <Typography
            sx={{
              borderRadius: "16px",

              padding: "15px",
              height: "70px",
              marginBottom: "16px",
              fontFamily: "IRANSansX",
              color: theme.customColors.neutral[50],
              fontSize: "12px",
              background: theme.customColors.neutral[98],
            }}
          >
            لطفاً با نام کاربری و کلمه عبوری که از طریق پیامک برای شما ارسال
            می‌شود وارد سامانه ثمین شوید.
          </Typography>
        </Grid>
        <Grid size={12}>
          <PrimaryCustomButton
            value={"ثبت نام"}
            onClickHandler={handleSaveRegister}
            buttonType={ButtonType.actionButton}
            customButtonStyle={{
              borderRadius: "12px",
              width: "97%",
            }}
            textStyle={{
              fontFamily: "IRANSansX",
            }}
          />
        </Grid>
      </Grid>

      <Typography
        variant="body2"
        textAlign="center"
        sx={{ color: theme.palette.text.secondary, fontFamily: "IRANSansX" }}
      >
        <Box component="span" sx={{ marginRight: 0.5 }}>
          حساب کاربری دارید؟
        </Box>
        <CustomLink title="ورود" href="/" {...customLinkProps(theme)} />
      </Typography>
    </>
  );
};

export default Register;
